import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableRowActions } from "./DataTableRowAction";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { displayDate, displayValue } from "@/utils/Helper";
import { useToast } from "@/components/ui/UseToast";
import { Checkbox } from "@/components/ui/CheckBox";
import { Button } from "@/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import { Status, useGetStaffListQuery } from "@/services/StaffsApi";
import React, { useEffect, useMemo, useState } from "react";
import { Task, TaskPriority, TaskStatus } from "@/app/types/tasks";
import TaskForm from "../modal/TaskForm";
import { useGetAllTasksQuery } from "@/services/TasksApi";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { useGetAllListingOfTicketsQuery } from "@/services/LiveQueueService";
import TaskLogs from "./TaskLogs";
import DetailLogs from "@/components/admin/tickets/ticket-detail/AuditLog";

// initial filter setting
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

const initialTicketValue = {
  sort_order: "desc",
  sort_key: "id",
};

const TasksManagementTable = () => {
  const [openLog, setOpenLog] = useState(false);
  const [taskId, setTaskID] = useState<undefined | Number>();
  //access level
  const viewkanban = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).viewkanban ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const { data: staffData } = useGetStaffListQuery({query:""});
  const [taskLogsOpen,setTasksLogOpen]=useState(false);
  const [ticketId, setTicketId] = React.useState<number | undefined>();

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<Task | undefined>(undefined);

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: TaskData,
    isLoading: isTaskDataLoading,
    refetch,
    error,
    isError,
  } = useGetAllTasksQuery(
    { query },
    {
      skip: query == "",
    }
  );

  const tableData = useMemo(() => {
    return Array.isArray(TaskData?.data) ? TaskData?.data : [];
  }, [TaskData?.data]);

  const handleOpenForm = (payload: Task) => {
    setAction("edit");
    setData({ ...payload, task_id: payload.task_id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Task> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={viewkanban}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
        setOpenLog={setTasksLogOpen}
        setTicketId={setTicketId}
      />
    ),
  };

  const columns: ColumnDef<Task>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: boolean) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "task_title",
      meta: "task_title",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Title"
          sortKey="title"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <>
            {viewkanban === "write" ? (
              <button
                onClick={() => handleOpenForm(row.original)}
                className=" underline"
              >
                <Tooltip>
                  <TooltipTrigger>
                    <p className="capitalize underline text-nowrap max-w-[150px] truncate overflow-hidden">
                      {row.original.title}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>{row.original.title}</TooltipContent>
                </Tooltip>
              </button>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger>
                    <p className="capitalize text-nowrap max-w-[150px] truncate overflow-hidden">
                      {row.original.title}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>{row.original.title}</TooltipContent>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "linked_ticket",
      meta: "linked_ticket",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Linked Ticket"
          sortKey="ticket_id"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p
            className="capitalize text-nowrap underline cursor-pointer"
            onClick={() => {
              setTaskID(Number(row.original.linked_ticket?.ticket_id));
              setOpenLog(true);
            }}
          >
            {displayValue(row.original.linked_ticket?.ticket_number)}
          </p>
        );
      },
    },
    {
      accessorKey: "assigned to",
      meta: "assigned_to",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Assigned To"
          sortKey="assignedto"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[150px] truncate overflow-hidden">
                {staffData?.find(
                  (staff) => staff.value === row.original.assigned_to
                )?.label || ""}
              </p>
            </TooltipTrigger>
            <TooltipContent>
              {staffData?.find(
                (staff) => staff.value === row.original.assigned_to
              )?.label || ""}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "Due_Date",
      meta: "due_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Due Date"
          sortKey="due_date"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="capitalize text-nowrap">
            {new Date(row.original.due_date)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}
          </p>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p
                className={cn(
                  "capitalize text-nowrap max-w-[150px] truncate overflow-hidden"
                )}
              >
                <Badge
                  className={cn(
                    row.original.status === TaskStatus.TODO
                      ? "bg-[#3dc7d1]"
                      : row.original.status === TaskStatus.IN_PROGRESS
                        ? "bg-[#63c82c]"
                        : row.original.status === TaskStatus.HOLD
                          ? "bg-[#ff9429]"
                          : row.original.status === TaskStatus.COMPLETED
                            ? "bg-[#1d4a18]"
                            : row.original.status === TaskStatus.ARCHIVED
                              ? "bg-[#cf3040]"
                              : ""
                  )}
                >
                  {row.original.status
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())
                    .replace(/^Todo$/, "Pending")}
                </Badge>
              </p>
            </TooltipTrigger>
            <TooltipContent>{row.original.status}</TooltipContent>
          </Tooltip>
        );
      },
    },

    {
      accessorKey: "priority",
      meta: "priority",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Priority"
          sortKey="priority"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="capitalize text-nowrap">
            <Badge
              className={cn(
                row.original.priority === TaskPriority.LOW
                  ? "bg-[#4CAF50]"
                  : row.original.priority === TaskPriority.MEDIUM
                    ? "bg-[#FFC107]"
                    : row.original.priority === TaskPriority.HIGH
                      ? "bg-[#F44336]"
                      : ""
              )}
            >
              {displayValue(row.original.priority)}
            </Badge>
          </p>
        );
      },
    },

    ...(viewkanban !== "read" ? [actionsColumn] : []),
  ];
  const totalRecords = TaskData?.filtered_count ?? 0;

  useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };
  const {
    searchCriteria: ticketSearchCriteria,
    query: ticketQuery,
    setSearchCriteria: setTicketSearchCriteria,
  } = useTableData(initialTicketValue);
  const { data: ticketData, isLoading: isDepartmentDataLoading } =
    useGetAllListingOfTicketsQuery(
      {
        query: ticketQuery,
      },
      {
        skip: ticketQuery == "",
      }
    );

  const ticketOption = React.useMemo(() => {
        return Array.isArray(ticketData?.data) ? ticketData?.data : [];
      }, [ticketData])

const [selectedLabels, setSelectedLabels] = useState<{
  [key: string]: string[];
}>({});

  const handleFilterChange = (
    filterName: string,
    values: string[],
    options?: any[]
  ) => {
    setFilterData((prevFilters) => ({
      ...prevFilters,
      [filterName]: values, // Store selected values
    }));

    // Store selected labels
    const selectedOptions = options?.filter((option) =>
      values.includes(option.value)
    );
    setSelectedLabels((prevLabels) => ({
      ...prevLabels,
      [filterName]: selectedOptions ? selectedOptions.map((option) => option.label) : [],
    }));
  };


  const filterDisplay = [
    {
      type: "multiselect",
      name: "status",
      label: "Status",
      options: [
        { value: "todo", label: "Pending" },
        { value: "in_progress", label: "In-Progress" },
        { value: "hold", label: "Hold" },
        { value: "completed", label: "Completed" },
        { value: "archived", label: "Archived" },
      ],
      function: (value: string[]) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "assigned_to",
      label: "Assigned To",
      options: staffData?.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: string[]) => handleFilterChange("assigned_to", value),
    },
    {
      type: "multiselect",
      name: "priority",
      label: "Priority",
      options: [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
      ],
      function: (value: string[]) => handleFilterChange("priority", value),
    },
    {
      type: "multiselect",
      name: "ticket_id",
      label: "Linked Tickets",
      setSearchCriteria: setTicketSearchCriteria,
      selectedValues: selectedLabels["ticket_id"] || [], // Ensure it refers to the correct filter
      setSelectedValues: setSelectedLabels,
      options: ticketOption?.map((item) => ({
        value: item.id,
        label: item.ticket_id,
      })),
      function: (value: string[]) =>
        handleFilterChange(
          "ticket_id",
          value,
          ticketOption?.map((item) => ({
            value: item.id,
            label: item.ticket_id,
          }))
        ),
    },
  ];

  return (
    <>
      {/* lg:flex-row */}
      <div className="flex flex-row  lg:items-center justify-between gap-4 px-3 pt-2">
        {/* Table Filter component */}
        <TableSearchFilter
          inputValue={inputValue}
          setInputValue={setInputValue}
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search"}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={viewkanban !== "read"}
            className="bg-primary text-sm text-nowrap  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={handleOpen}
          >
            <i className="fa-solid fa-plus" />
            {""}
            Create New
          </Button>

          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="capitalize">click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isTaskDataLoading}
          searchMessage="No Task Found"
        />
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      {/* table filters */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      {/* form */}
      <TaskForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
        isgettingCreate={false}
      />
      <TaskLogs
        isOpen={taskLogsOpen}
        setOpen={setTasksLogOpen}
        ticketId={ticketId}
      />
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={taskId} />
    </>
  );
};

export default TasksManagementTable;
